import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import { Button } from '@kizen/kds/Button';
import Loader from '__components/Kizen/Loader';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import {
  useMenuList,
  LOAD_THRESHOLD,
} from 'ts-components/filters/hooks/useMenuList';
import { ChatSessionHistoryItem } from '../types';
import { SessionHistoryItem } from './SessionHistoryItem';
import { DeleteSessionModal } from './DeleteSessionModal';
import KizenAILogo from 'pages/Marketplace/assets/kizen-ai.svg?react';

type SessionHistoryPanelProps = {
  sessions: ChatSessionHistoryItem[];
  currentSessionId: string;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  onClose: () => void;
  onSelectSession: (session: ChatSessionHistoryItem) => void;
  onDeleteSession: (sessionId: string) => Promise<void>;
  headerLogoUrl: string | null;
};

export const SessionHistoryPanel: FC<SessionHistoryPanelProps> = ({
  sessions,
  currentSessionId,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  onClose,
  onSelectSession,
  onDeleteSession,
  headerLogoUrl,
}) => {
  const { t } = useTranslation();
  const [sessionToDelete, setSessionToDelete] =
    useState<ChatSessionHistoryItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { setMenuList } = useMenuList(
    isFetchingNextPage,
    fetchNextPage,
    LOAD_THRESHOLD,
    hasNextPage
  );

  const handleDeleteClick = (session: ChatSessionHistoryItem) => {
    setSessionToDelete(session);
  };

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;

    setIsDeleting(true);
    try {
      await onDeleteSession(sessionToDelete.id);
    } finally {
      setIsDeleting(false);
      setSessionToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setSessionToDelete(null);
  };

  return (
    <>
      <div
        className="flex flex-col h-full w-full bg-white"
        data-qa-id="session-history-panel"
      >
        <div
          className="flex w-full p-[10px] gap-[10px] h-[50px] items-center"
          style={{
            background:
              'linear-gradient(to right, var(--color-ai-gradient-start), var(--color-ai-gradient-end))',
          }}
        >
          <div className="flex justify-between items-center w-full">
            <div className="flex items-center gap-[10px]">
              <div className="flex w-[30px] h-[30px]">
                {headerLogoUrl ? (
                  <img
                    src={headerLogoUrl}
                    alt="Kizen AI"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <KizenAILogo className="w-full h-full" />
                )}
              </div>
              <Typography size="2xl" weight="semibold" color="ai-system-text">
                {t('Kizen AI - Recent Chats')}
              </Typography>
            </div>
            <div className="flex items-center mr-[5px]">
              <Button
                variant="text"
                color="tertiary"
                size="medium"
                rightIcon="action-collapse-frame"
                onClick={onClose}
                data-qa-id="close-session-history-panel"
                rightIconSettings={{
                  tooltipEnabled: false,
                }}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col overflow-hidden relative flex-1">
          <div className="absolute top-0 left-0 w-full pointer-events-none">
            <Gradient
              fadeStart={0}
              fadeLength={20}
              color="var(--color-background-white)"
              position="top"
              fadeIn
              zIndex={2}
              borderRadius="0"
            />
          </div>
          <div className="absolute bottom-0 left-0 w-full pointer-events-none">
            <Gradient
              fadeStart={0}
              fadeLength={20}
              color="var(--color-background-white)"
              position="bottom"
              fadeIn
              zIndex={2}
            />
          </div>
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader loading />
            </div>
          ) : sessions.length === 0 ? (
            <div className="flex-1 flex items-center justify-center px-4">
              <p className="text-background-medium text-center">
                {t('No previous sessions found')}
              </p>
            </div>
          ) : (
            <div
              className="flex-1 overflow-y-auto kds-expanding-scrollbar animate-fadeIn pt-[5px]"
              ref={(el) => setMenuList(el)}
            >
              {sessions.map((session) => (
                <SessionHistoryItem
                  key={session.id}
                  session={session}
                  isActive={session.id === currentSessionId}
                  onClick={() => onSelectSession(session)}
                  onDelete={() => handleDeleteClick(session)}
                />
              ))}
              {isFetchingNextPage && (
                <div className="flex justify-center py-4">
                  <Loader loading />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {sessionToDelete && (
        <DeleteSessionModal
          onHide={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
};
